/*
 * Copyright (c) All right reserved Anvish Inc. 2025
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */
import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Spinner } from "@heroui/spinner";
import { Button } from "@heroui/button";

import { FetchDataServerAction } from "@/services/FetchDataServerAction";
import FilterSelect from "@/components/element/CategoryTabbedFilter/FilterSelect";

interface TyreSizeFilterProps {
  redirectUrl: string;
  csrfToken: string;
  isSingleColumn?: boolean;
}

const TyreSizeFilter: React.FC<TyreSizeFilterProps> = ({
  redirectUrl,
  csrfToken,
  isSingleColumn = false,
}) => {
  const router = useRouter();
  const [sectionWidthList, setSectionWidthList] = useState<number[]>([]);
  const [aspectRatioList, setAspectRatioList] = useState<string[]>([]);
  const [rimSizeList, setRimSizeList] = useState<number[]>([]);
  const [selectedFilters, setSelectedFilters] = useState({
    section_width: "",
    aspect_ratio: "",
    rim_size: "",
  });
  const [selectedTyreSize, setSelectedTyreSize] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const getFilterData = async ({
    trigger = null,
    filters = {},
  }: {
    trigger: string | null;
    filters: any;
  }) => {
    setIsLoading(true);
    setSelectedFilters(filters);
    const resp = await FetchDataServerAction(
      csrfToken,
      "ecomm/tyre-size-filters/",
      { filter: filters },
    );

    if (!trigger) {
      setSectionWidthList(resp);
    }
    if (trigger === "section_width") {
      setAspectRatioList(resp);
    }
    if (trigger === "aspect_ratio") {
      setRimSizeList(resp);
    }
    if (trigger === "rim_size") {
      setSelectedTyreSize(resp[0].tyre_size);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (sectionWidthList.length === 0) {
      getFilterData({ trigger: null, filters: {} });
    }
  }, []);

  const handleFilterChange = (trigger: string, value: string) => {
    let filterData: any = {};

    if (trigger === "section_width") {
      setAspectRatioList([]);
      setRimSizeList([]);
      setSelectedTyreSize(null);
      filterData = {
        section_width: value,
        aspect_ratio: "",
        rim_size: "",
      };

      getFilterData({ trigger: "section_width", filters: filterData });
    }
    if (trigger === "aspect_ratio") {
      setRimSizeList([]);
      setSelectedTyreSize(null);
      filterData = {
        ...selectedFilters,
        aspect_ratio: value,
        rim_size: "",
      };

      getFilterData({ trigger: "aspect_ratio", filters: filterData });
    }

    if (trigger === "rim_size") {
      setSelectedTyreSize(null);
      filterData = {
        ...selectedFilters,
        rim_size: value,
      };

      getFilterData({ trigger: "rim_size", filters: filterData });
    }
  };

  const handleReset = () => {
    setSelectedFilters({
      section_width: "",
      aspect_ratio: "",
      rim_size: "",
    });
    setAspectRatioList([]);
    setRimSizeList([]);
    setIsLoading(false);
    setSelectedTyreSize(null);
  };

  return (
    <div className="flex flex-col gap-5 items-center justify-start">
      {isLoading && (
        <div className="flex absolute top-0 left-0 w-full h-full items-center justify-center z-10 bg-black/20">
          <Spinner color="primary" size="lg" />
        </div>
      )}
      <div
        className={`grid grid-cols-1 w-full gap-3 md:gap-5 ${isSingleColumn ? "" : "md:grid-cols-2"}`}
      >
        {" "}
        <FilterSelect
          label="Section Width"
          options={sectionWidthList}
          selectedKeys={[selectedFilters.section_width]}
          onSelectionChange={(keys) =>
            handleFilterChange("section_width", keys.currentKey || "")
          }
        />
        <FilterSelect
          isDisabled={!selectedFilters.section_width}
          label="Aspect Ratio"
          options={aspectRatioList}
          selectedKeys={[selectedFilters.aspect_ratio]}
          onSelectionChange={(keys) =>
            handleFilterChange("aspect_ratio", keys.currentKey || "")
          }
        />
        <FilterSelect
          isDisabled={!selectedFilters.aspect_ratio}
          label="Rim Size"
          options={rimSizeList}
          selectedKeys={[selectedFilters.rim_size]}
          onSelectionChange={(keys) =>
            handleFilterChange("rim_size", keys.currentKey || "")
          }
        />
      </div>
      <div className="flex items-center justify-center w-full gap-3 md:gap-5">
        <Button
          className="font-medium md:font-normal text-base md:text-lg bg-[#F5F5F5] border-1 border-[#939393] hover:bg-[#FAFAFA] p-2 md:p-5 w-[120px] md:w-[160px] lg:w-[200px]"
          variant="bordered"
          onPress={handleReset}
        >
          Reset
        </Button>
        <Button
          className="font-medium md:font-normal text-base md:text-lg bg-ax-primary hover:opacity-80 border-1 border-ax-primary p-2 md:p-5 w-[120px] md:w-[160px] lg:w-[200px]"
          isDisabled={!selectedTyreSize}
          variant="flat"
          onPress={() =>
            router.push(
              `${redirectUrl}?tyre_size=${selectedTyreSize}&section_width=${selectedFilters.section_width}&aspect_ratio=${selectedFilters.aspect_ratio}&rim_size=${selectedFilters.rim_size}`,
            )
          }
        >
          Search
        </Button>
      </div>
    </div>
  );
};

export default TyreSizeFilter;
